import React from 'react';
import * as yup from 'yup';

import { SLUG_REGEX, URL_REGEX } from 'constants/Misc';
import { default as I } from './IntlMessages';

const today = new Date();
today.setHours(0, 0, 0, 0);

const NAME_REQUIRED = <I id="form.error.name.required" />;
const EMAIL_REQUIRED = <I id="form.error.email.required" />;
const EMAIL_INVALID = <I id="form.error.email.invalid" />;
const SLUG_REQUIRED = <I id="form.error.slug.required" />;
const SLUG_INVALID = <I id="form.error.slug.invalid" />;
const WALLET_REQUIRED = <I id="form.error.wallet.required" />;
const CURRENCY_REQUIRED = <I id="form.error.currency.required" />;
const ROLE_REQUIRED = <I id="form.error.role.required" />;
const PASSWORD_REQUIRED = <I id="form.error.password.required" />;
const PASSWORD_INVALID = <I id="form.error.password.invalid" />;
const PASS_CONF_REQ = <I id="form.error.password_confirmation.required" />;
const PASS_CONF_INVALID = <I id="form.error.password_confirmation.invalid" />;
const NEW_PASSWORD_REQUIRED = <I id="form.error.new_password.required" />;
const CLIENT_ID_REQUIRED = <I id="form.error.client_id.required" />;
const GENERAL_REQUIRED = <I id="form.error.general.required" />;
const CLIENT_SECRET_REQUIRED = <I id="form.error.client_secret.required" />;
const BASE_URL_REQUIRED = <I id="form.error.base_url.required" />;
const URL_INVALID = <I id="form.error.url.invalid" />;
const LOGIN_PIX_PATH_REQUIRED = <I id="form.error.login_pix_path.required" />;
const CREATE_PIX_PATH_REQUIRED = <I id="form.error.create_pix_path.required" />;
const WITHDRAWAL_PATH_REQ = <I id="form.error.withdrawal_pix_path.required" />;
const PIX_KEY_REQUIRED = <I id="form.error.pix_key.required" />;
const ASS_MERCHANT_REQUIRED = <I id="form.error.associatedMerchant.required" />;
const CUSTOMER_ID_REQUIRED = <I id="form.error.customer_id.required" />;
const TRANSACTION_ID_REQUIRED = <I id="form.error.transaction_id.required" />;
const BANK_ID_REQUIRED = <I id="form.error.bank_id.required" />;
const INVALID_ID = <I id="form.error.invalid_id" />;
const CBU_NUMBER_REQUIRED = <I id="form.error.cbu_number.required" />;
const TRANSACTION_REQUIRED = <I id="form.error.transaction_type.required" />;
const ERROR_REQUIRED = <I id="error.required" />;
const ERROR_FUTURE_DATE = <I id="error.future-date-error" />;
const ERROR_END_DATE_INVALID = <I id="error.end-date-less-than-start" />;

const USER_BASIC_INFO_SCHEMA = {
  name: yup.string().required(NAME_REQUIRED),
  email: yup
    .string()
    .email(EMAIL_INVALID)
    .required(EMAIL_REQUIRED),
  password: yup
    .string()
    .required(PASSWORD_REQUIRED)
    .min(8, PASSWORD_INVALID)
    .matches(/[a-z]+/, { message: PASSWORD_INVALID })
    .matches(/[A-Z]+/, { message: PASSWORD_INVALID })
    .matches(/\d+/, { message: PASSWORD_INVALID }),
  password_confirmation: yup
    .string()
    .required(PASS_CONF_REQ)
    .oneOf([yup.ref('password'), null], PASS_CONF_INVALID),
};

const UPDATE_PASSWORD_BASE_SCHEMA = {
  new_password: yup
    .string()
    .required(NEW_PASSWORD_REQUIRED)
    .min(8, PASSWORD_INVALID)
    .matches(/[a-z]+/, { message: PASSWORD_INVALID })
    .matches(/[A-Z]+/, { message: PASSWORD_INVALID })
    .matches(/\d+/, { message: PASSWORD_INVALID }),
  new_password_confirmation: yup
    .string()
    .required(PASS_CONF_REQ)
    .oneOf([yup.ref('new_password'), null], PASS_CONF_INVALID),
};

export const SIGN_IN_SCHEMA = yup.object({
  email: yup
    .string()
    .email(EMAIL_INVALID)
    .required(EMAIL_REQUIRED),
  password: yup.string().required(PASSWORD_REQUIRED),
});

export const FORGOT_PASSWORD_SCHEMA = yup.object({
  email: yup
    .string()
    .email(EMAIL_INVALID)
    .required(EMAIL_REQUIRED),
});

export const PASSWORD_RECOVERY_SCHEMA = yup.object(UPDATE_PASSWORD_BASE_SCHEMA);

export const PASSWORD_RESET_SCHEMA = yup.object({
  ...UPDATE_PASSWORD_BASE_SCHEMA,
  current_password: yup.string().required(PASSWORD_REQUIRED),
});

export const CREATE_MERCHANT_SCHEMA = yup.object({
  name: yup.string().required(NAME_REQUIRED),
  slug: yup
    .string()
    .required(SLUG_REQUIRED)
    .matches(SLUG_REGEX, {
      message: SLUG_INVALID,
    }),
  company_name: yup.string(),
  country_iso: yup.string().nullable(),
  industry: yup.string().nullable(),
  wallets: yup
    .array()
    .min(1, WALLET_REQUIRED)
    .required(WALLET_REQUIRED),
  currencies: yup
    .array()
    .min(1, CURRENCY_REQUIRED)
    .required(CURRENCY_REQUIRED),
  ips: yup
    .array()
    .of(yup.string())
    .nullable()
    .default([]),
  banks_order: yup
    .object()
    .shape({
      deposit: yup.object().shape({
        strict: yup.boolean(),
        list: yup.lazy((obj) =>
          yup.object().shape(
            Object.keys(obj || {}).reduce((acc, key) => {
              acc[key] = yup
                .number()
                .typeError(GENERAL_REQUIRED)
                .min(0, 'Min: 0')
                .max(100, 'Max: 100')
                .required(GENERAL_REQUIRED);
              return acc;
            }, {})
          )
        ),
      }),
      withdrawal: yup.object().shape({
        strict: yup.boolean(),
        list: yup.lazy((obj) =>
          yup.object().shape(
            Object.keys(obj || {}).reduce((acc, key) => {
              acc[key] = yup
                .number()
                .typeError(GENERAL_REQUIRED)
                .min(0, 'Min: 0')
                .max(100, 'Max: 100')
                .required(GENERAL_REQUIRED);
              return acc;
            }, {})
          )
        ),
      }),
    })
    .nullable()
    .default(null),
});

export const UPDATE_MERCHANT_SCHEMA = yup.object({
  name: yup.string().required(NAME_REQUIRED),
  company_name: yup.string().nullable(),
  country_iso: yup.string().nullable(),
  industry: yup.string().nullable(),
  slug: yup
    .string()
    .required(SLUG_REQUIRED)
    .matches(SLUG_REGEX, {
      message: SLUG_INVALID,
    }),
  ips: yup
    .array()
    .of(yup.string())
    .nullable()
    .default([]),
  banks_order: yup
    .object()
    .shape({
      deposit: yup.object().shape({
        strict: yup.boolean(),
        list: yup.object().test('all-percents-filled', GENERAL_REQUIRED, (value) => {
          if (!value) return true;
          return Object.values(value).every((percent) => percent !== '' && percent !== null && !isNaN(percent));
        }),
      }),
      withdrawal: yup.object().shape({
        strict: yup.boolean(),
        list: yup.object().test('all-percents-filled', GENERAL_REQUIRED, (value) => {
          if (!value) return true;
          return Object.values(value).every((percent) => percent !== '' && percent !== null && !isNaN(percent));
        }),
      }),
    })
    .nullable()
    .default(null),
});

export const MERCHANT_CURRENCIES_SCHEMA = yup.object({
  currencies: yup
    .array()
    .min(1, CURRENCY_REQUIRED)
    .required(CURRENCY_REQUIRED),
});

export const CREATE_ADMIN_USER_SCHEMA = yup.object({
  ...USER_BASIC_INFO_SCHEMA,
  roles: yup
    .array()
    .min(1, ROLE_REQUIRED)
    .required(ROLE_REQUIRED),
});

export const CREATE_MERCHANT_USER_SCHEMA = yup.object({
  name: yup.string().required(NAME_REQUIRED),
  email: yup
    .string()
    .email(EMAIL_INVALID)
    .required(EMAIL_REQUIRED),
  associatedMerchant: yup.object().required(ASS_MERCHANT_REQUIRED),
});

export const CREATE_OWN_MERCHANT_USER_SCHEMA = yup.object({
  name: yup.string().required(NAME_REQUIRED),
  email: yup
    .string()
    .email(EMAIL_INVALID)
    .required(EMAIL_REQUIRED),
});

export const UPDATE_USER_SCHEMA = yup.object({
  name: yup.string().required(NAME_REQUIRED),
  email: yup
    .string()
    .email(EMAIL_INVALID)
    .required(EMAIL_REQUIRED),
});

export const CREATE_BANK_SCHEMA = yup.object({
  name: yup.string().required(NAME_REQUIRED),
  slug: yup
    .string()
    .required(SLUG_REQUIRED)
    .matches(SLUG_REGEX, {
      message: SLUG_INVALID,
    }),
  integration_info: yup.object().shape({
    client_id: yup.string().required(CLIENT_ID_REQUIRED),
    client_secret: yup.string().required(CLIENT_SECRET_REQUIRED),
    base_url: yup
      .string()
      .required(BASE_URL_REQUIRED)
      .matches(URL_REGEX, {
        message: URL_INVALID,
      }),
    login_pix_path: yup.string().required(LOGIN_PIX_PATH_REQUIRED),
    create_pix_path: yup.string().required(CREATE_PIX_PATH_REQUIRED),
    withdrawal_pix_path: yup.string().required(WITHDRAWAL_PATH_REQ),
    pix_key: yup.string().required(PIX_KEY_REQUIRED),
  }),
});

export const UPDATE_BANK_SCHEMA = yup.object({
  name: yup.string().required(NAME_REQUIRED),
  slug: yup
    .string()
    .required(SLUG_REQUIRED)
    .matches(SLUG_REGEX, {
      message: SLUG_INVALID,
    }),
});

const createRequiredField = () => yup.number().nullable(true);

const createPercentField = () =>
  yup
    .number()
    .nullable(true)
    .min(0, 'Min: 0')
    .max(100, 'Max: 100');

export const createBankPaymentMethodSchema = (validCurrencies) => {
  const currencyList = Array.isArray(validCurrencies) ? validCurrencies.map((c) => String(c).toUpperCase()) : [];

  return yup.object().shape({
    payment_method_id: yup
      .number()
      .required('component.bankAssociatedMethods.errors.methodRequired')
      .positive('component.bankAssociatedMethods.errors.methodRequired')
      .typeError('component.bankAssociatedMethods.errors.methodRequired'),

    payment_method_name: yup.string().nullable(),

    currency_iso: yup
      .string()
      .nullable()
      .test('has-valid-length-and-exists', 'component.bankAssociatedMethods.errors.currencyInvalidOrEmpty', function(
        value
      ) {
        if (!value) {
          return true;
        }

        const isValidLength = value.length >= 3;
        const existsInList = currencyList.includes(value.toUpperCase());

        return isValidLength && existsInList;
      }),

    available_for_deposit: yup.boolean().required(),
    available_for_withdrawal: yup.boolean().required(),

    transaction_limits: yup
      .object()
      .shape({
        deposit: yup
          .object()
          .shape({
            min: createRequiredField('deposit.min'),
            max: createRequiredField('deposit.max'),
          })
          .nullable(true),
        withdrawal: yup
          .object()
          .shape({
            min: createRequiredField('withdrawal.min'),
            max: createRequiredField('withdrawal.max'),
          })
          .nullable(true),
      })
      .nullable(true),

    fees: yup
      .object()
      .shape({
        deposit: yup
          .object()
          .shape({
            fixed: yup.number().nullable(true),
            percent: createPercentField('deposit.percent'),
          })
          .nullable(true),
        withdrawal: yup
          .object()
          .shape({
            fixed: yup.number().nullable(true),
            percent: createPercentField('withdrawal.percent'),
          })
          .nullable(true),
      })
      .nullable(true),
  });
};

export const CREATE_CBU_SCHEMA = yup.object({
  bank_id: yup
    .number()
    .min(0, INVALID_ID)
    .nullable()
    .typeError(INVALID_ID)
    .required(BANK_ID_REQUIRED),
  number: yup
    .string()
    .trim()
    .required(CBU_NUMBER_REQUIRED),
  external_id: yup.string().trim(),
  holder: yup.string().trim(),
  active_deposit: yup.boolean(),
  active_withdrawal: yup.boolean(),
});

export const UPDATE_CBU_SCHEMA = yup.object({
  external_id: yup.string().trim(),
  holder: yup.string().trim(),
  active_deposit: yup.boolean(),
  active_withdrawal: yup.boolean(),
});

export const CREATE_CUSTOMER_BANK_SCHEMA = yup.object({
  customer_id: yup
    .number()
    .min(0, INVALID_ID)
    .nullable()
    .typeError(INVALID_ID)
    .required(CUSTOMER_ID_REQUIRED),
  bank_id: yup
    .number()
    .min(0, INVALID_ID)
    .nullable()
    .typeError(INVALID_ID)
    .required(BANK_ID_REQUIRED),
  transaction_type: yup.string().required(TRANSACTION_REQUIRED),
});

export const CONCILIATE_SCHEMA = yup.object().shape({
  transaction_id: yup
    .number()
    .typeError(TRANSACTION_ID_REQUIRED)
    .required(TRANSACTION_ID_REQUIRED),
});

export const CREATE_CASHOUT_SCHEMA = yup.object({
  amount: yup
    .number()
    .typeError(<I id="form.error.cashout.amount.invalid" />)
    .required(<I id="form.error.cashout.amount.required" />)
    .min(1, <I id="form.error.cashout.amount.min" />)
    .max(200000000, <I id="form.error.cashout.amount.max" />),
  bank_account: yup
    .string()
    .required(<I id="form.error.cashout.bank_account.required" />)
    .matches(/^\d{22}$/, {
      message: <I id="form.error.cashout.bank_account.invalid" />,
    }),
  document_number: yup
    .string()
    .required(<I id="form.error.cashout.document_number.required" />)
    .matches(/^\d{11}$/, {
      message: <I id="form.error.cashout.document_number.invalid" />,
    }),
  provider: yup.string().nullable(),
  description: yup.string().nullable(),
});

export const PERIOD_CONCILIATION_SCHEMA = yup.object().shape({
  internal_begin: yup
    .date()
    .typeError(ERROR_REQUIRED)
    .required(ERROR_REQUIRED)
    .test('not-future', ERROR_FUTURE_DATE, (value) => {
      if (!value) return true;
      return value <= new Date();
    }),

  internal_end: yup
    .date()
    .typeError(ERROR_REQUIRED)
    .required(ERROR_REQUIRED)
    .min(yup.ref('internal_begin'), ERROR_END_DATE_INVALID)
    .test('not-future', ERROR_FUTURE_DATE, (value) => {
      if (!value) return true;
      return value <= new Date();
    }),

  external_begin: yup
    .date()
    .typeError(ERROR_REQUIRED)
    .required(ERROR_REQUIRED)
    .test('not-future', ERROR_FUTURE_DATE, (value) => {
      if (!value) return true;
      return value <= new Date();
    }),

  external_end: yup
    .date()
    .typeError(ERROR_REQUIRED)
    .required(ERROR_REQUIRED)
    .min(yup.ref('external_begin'), ERROR_END_DATE_INVALID)
    .test('not-future', ERROR_FUTURE_DATE, (value) => {
      if (!value) return true;
      return value <= new Date();
    }),
});

export const NO_REQUIRED_VALIDATIONS = yup.object().shape({});
